import './root.css';
import fs from 'node:fs/promises';
import path from 'node:path';
import { fileURLToPath } from 'node:url';
import type { Props as FooterProps } from '@micro-server/ni-footer/settings';
import type { Props as HeaderProps } from '@micro-server/ni-header/settings';
import { captureException } from '@sentry/react-router';
import { clsx } from 'clsx';
import {
  COUNTRY_COOKIE,
  COUNTRY_HEADER,
  DEFAULT_LOCALE,
} from 'dotcom-core/src/constants';
import {
  getISOLocale,
  getUrlLocale,
  isValidUrlLanguage,
  replaceLanguageInUrl,
  supportedLanguages,
} from 'dotcom-core/src/helpers/locale';
import { useEffect, useRef } from 'react';
import {
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  isRouteErrorResponse,
  useLoaderData,
  useNavigate,
  useRouteError,
} from 'react-router';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import { GeneralErrorPage } from './components/GeneralErrorPage';
import { Micro } from './components/Micro';
import { guestCartIdParser } from './cookies.server';
import env from './env';
import type { Language } from './graphql';
import { checkAuthentication } from './helpers/auth.server';
import { getCartId } from './helpers/cart.server';
import {
  type GraphqlServerClient,
  getMicroFrontendData,
  makeClient,
} from './helpers/client';
import { getCookie } from './helpers/cookie';
import { mapGraphQLError } from './helpers/error';
import { ExperimentsScripts } from './helpers/experiments/ExperimentsScript';
import { initExperiments } from './helpers/experiments/experiments.server';
import { flattenMessages } from './helpers/intl';
import { isTruthy } from './helpers/predicate';

export const mobileModalRootId = 'mobile-modal-root';

function App() {
  const data = useLoaderData<RootLoaderData>();

  if ('error' in data) throw new Error(data.error);

  // @TODO: Remove this if not used, if we always use bg-white?

  // const { pathname } = useLocation();
  // const isAccountPage = useMatch(ROUTE_PATHS.account);
  // const isCartPage = useMatch(ROUTE_PATHS.cart);
  // const isCheckoutPage = [...Object.values(ROUTE_PATHS)].some((checkoutPath) =>
  //   matchPath(checkoutPath, pathname),
  // );

  return (
    <main>
      <PageHeader />
      <div
        className={clsx(
          'flex flex-col min-h-screen',
          'bg-white',
          // isAccountPage || isCartPage || isCheckoutPage
          //   ? 'bg-grey-10'
          //   : 'bg-white',
        )}
      >
        <div className="flex-auto">
          <Outlet />
        </div>
      </div>
      <Micro id="ni-footer" html={data.footerHtml} />
    </main>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<RootLoaderData>();

  if ('error' in data) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <Meta />
          <Links />
        </head>
        <body>
          {children}
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang={data.language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <ExperimentsScripts visitorCode={data.experimentUserId} />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.__ENVIRONMENT__ = ${JSON.stringify(data.ENV ?? {})};
            window['__BUILD_META__'] = typeof window['__BUILD_META__'] === 'object' ? window['__BUILD_META__'] : {};
            window['__BUILD_META__']['ni-v2'] = ${data.buildMeta};
        `,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

function PageHeader() {
  const html = useLoaderData().headerHtml;
  const microHeaderRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const microHeaderEl = microHeaderRef.current;

    if (!microHeaderEl) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      const isHeaderNavItem = !!target.getAttribute('data-ni-header-nav');
      const href = target.getAttribute('href');

      if (!(isHeaderNavItem && href)) return;

      event.preventDefault();
      event.stopPropagation();
      navigate({
        pathname: href.replace(window.location.origin, ''),
        search: window.location.search,
      });
    };

    microHeaderEl.addEventListener('click', handleClick);

    return () => {
      microHeaderEl.removeEventListener('click', handleClick);
    };
  }, [navigate]);

  return (
    <div id={mobileModalRootId} className="sticky top-0 z-50">
      {html ? <Micro id="ni-header" html={html} ref={microHeaderRef} /> : null}
    </div>
  );
}

function ErrorBoundary() {
  const data = useLoaderData();
  const error = useRouteError();
  const isDevelopment = import.meta.env.MODE === 'development';

  captureException(error);

  // biome-ignore lint/suspicious/noConsole: This warning is intentional
  console.error(
    `❗An error was caught by the error boundary. Check GCP or Sentry logs for more details. [${new Date().toLocaleString()}] `,
  );

  if (isDevelopment) {
    if (isRouteErrorResponse(error)) {
      return (
        <div>
          <h1 className="webex-h1">
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    }
    if (error instanceof Error) {
      return (
        <div>
          <h1 className="webex-h1">Unexpected Error</h1>
          <p>{error.message}</p>
        </div>
      );
    }
  }

  return (
    <div>
      <PageHeader />
      <GeneralErrorPage />
      {data?.footerHtml ? (
        <Micro id="ni-footer" html={data.footerHtml} />
      ) : null}
    </div>
  );
}

const __dirname = path.dirname(fileURLToPath(import.meta.url));
const resolve = (p: string) => path.resolve(__dirname, p);

async function loader(loaderArgs: LoaderFunctionArgs) {
  try {
    const { country, language } = getIntl(loaderArgs);

    const cookieHeader = loaderArgs.request.headers.get('Cookie');
    const buildMeta = await getBuildMeta();
    const client = makeClient(loaderArgs);
    const messages = await getTranslations({ language, client });

    const { headerHtml, footerHtml } = await getMicroFrontends({
      country,
      language,
      url: loaderArgs.request.url,
    });

    const { cartId, guestCartId } = await getCartId({ client, cookieHeader });
    const { isAuthenticated } = checkAuthentication(loaderArgs.request);
    const user = isAuthenticated ? await client.User().catch(() => null) : null;
    const { experimentUserId, experimentVariations } = await initExperiments({
      reqHeaders: new Headers(),
      resHeaders: loaderArgs.request.headers,
      user: user?.me,
    });

    return data(
      {
        ENV: {
          NODE_ENV: env.NODE_ENV,
          DEPLOYMENT_ENVIRONMENT: env.DEPLOYMENT_ENVIRONMENT,
          APP_BASE_URL: env.APP_BASE_URL,
          APP_TAG_MANAGER_PROFILE: env.APP_TAG_MANAGER_PROFILE,
          APP_COOKIE_CONSENT_APP_ID: env.APP_COOKIE_CONSENT_APP_ID,
          APP_GOOGLE_PLACES_API_KEY: env.APP_GOOGLE_PLACES_API_KEY,
          APP_ADYEN_CLIENT_KEY: env.APP_ADYEN_CLIENT_KEY,
          APP_SENTRY_AUTH_TOKEN: env?.APP_SENTRY_AUTH_TOKEN,
          APP_SENTRY_DNS: env?.APP_SENTRY_DSN,
          KAMELEOON_SITE_CODE: env?.KAMELEOON_SITE_CODE,
          LOG_LEVEL: env?.LOG_LEVEL,
        },
        nativeId: user?.me?.nativeId ?? null,
        ecommerceStatus: user?.me?.ecommerceStatus ?? null,
        buildMeta,
        language,
        cartId,
        country,
        messages,
        headerHtml,
        footerHtml,
        experimentUserId,
        experimentVariations,
      },
      {
        status: 200,
        headers: {
          'Set-Cookie': await guestCartIdParser.serialize(guestCartId),
        },
      },
    );
  } catch (error) {
    const errorMessage = mapGraphQLError({
      error,
      operationName: 'RootLoader',
    });

    return data(
      {
        error: `Failed to fetch required root data. Reason: ${errorMessage}`,
      },
      { status: 500 },
    );
  }
}

// root data is stable so we don't need revalidation
export const shouldRevalidate = () => false;

const links: LinksFunction = () => [
  {
    rel: 'shortcut icon',
    href: '/assets/favicon.ico',
  },
  {
    rel: 'kameleoon iframe',
    href: '/assets/kameleoon_static_file.html',
  },
];

const meta: MetaFunction = ({ location }) => {
  const url = `${env.APP_BASE_URL}${location.pathname}`;
  return [
    {
      tagName: 'link',
      rel: 'canonical',
      href: url,
    },
    ...supportedLanguages.map((lang) => {
      return {
        tagName: 'link',
        rel: 'alternate',
        hrefLang: lang,
        href: replaceLanguageInUrl(url, getUrlLocale(lang)),
      };
    }),
    {
      tagName: 'link',
      rel: 'alternate',
      hrefLang: 'x-default',
      href: replaceLanguageInUrl(url, DEFAULT_LOCALE),
    },
  ];
};

async function getTranslations({
  language,
  client,
}: { language: Language; client: GraphqlServerClient }) {
  const { translations } = await client.Translations({ language });
  const messages = flattenMessages(translations) ?? {};

  if (!isTruthy(Object.keys(messages).length)) {
    throw new Error('Failed to fetch translations.');
  }

  return messages;
}

function getIntl({ request, params }: LoaderFunctionArgs) {
  const cookieHeader = request.headers.get('Cookie');
  const language = params.language as Language; // always enforced by route
  const country =
    getCookie(cookieHeader, COUNTRY_COOKIE) ||
    request.headers.get(COUNTRY_HEADER);

  if (!country) {
    throw new Error(
      `Invalid country. Please provide a ${COUNTRY_COOKIE} cookie with a valid country code (e.g. "DE", "FR").`,
    );
  }

  if (!language || !isValidUrlLanguage(language)) {
    throw new Error(
      `Invalid language: ${language}. Please provide a valid language before the page path (e.g. "/en/page-path", "/de/page-path")`,
    );
  }

  return { country, language };
}

async function getBuildMeta() {
  let buildMeta = '"unavailable"';

  try {
    buildMeta = await fs.readFile(resolve('../build.json'), 'utf-8');
  } catch {
    // fail silently as this is not critical data
  }

  return buildMeta;
}

async function getMicroFrontends({
  country,
  language,
  url,
}: { country: string; language: Language; url: Request['url'] }) {
  const [headerHtml = '', footerHtml = ''] = await Promise.all([
    getMicroFrontendData<HeaderProps>({
      id: 'ni-header',
      language: getISOLocale(language),
      country,
      catalogLinks: url.includes(ROUTE_SEGMENTS.catalog),
    }),
    getMicroFrontendData<FooterProps>({
      id: 'ni-footer',
      language: getISOLocale(language),
      withBanner: !url.includes(ROUTE_SEGMENTS.pricing),
      type: 'reduced',
    }),
  ]);

  return { headerHtml, footerHtml };
}

type RootLoaderData = typeof loader;

export { loader, links, Layout, meta, ErrorBoundary, type RootLoaderData };

export default App;
